import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MagnifyingGlass, FileText, Plus, X, UploadSimple } from "@phosphor-icons/react";
import { SkeletonRows } from "@/components/RegisterSkeleton";

const inp = "h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
const CTYPES = ["Licence", "Ticket", "Induction", "Medical", "VOC"];

function itemStatus(c) {
  if (!c.expiry_date) return "no_expiry";
  const days = Math.floor((new Date(c.expiry_date) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  return days < 0 ? "expired" : days <= 30 ? "expiring" : "current";
}
const fmtd = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtTs = (d) => { try { return new Date(d).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", ""); } catch { return d; } };

function StatusPill({ status }) {
  if (status === "expired") return <span className="px-2 py-0.5 text-[11px] font-bold uppercase rounded-full border bg-[#FBE7E4] text-[#B42318] border-[#F1C4BC]">Expired</span>;
  if (status === "expiring") return <span className="px-2 py-0.5 text-[11px] font-bold uppercase rounded-full border bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]">Expiring</span>;
  if (status === "no_expiry") return <span className="px-2 py-0.5 text-[11px] font-bold uppercase rounded-full border bg-surface-pressed text-ink-secondary border-border">No expiry</span>;
  return <span className="inline-flex items-center gap-1.5 text-cell text-ink-secondary"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Current</span>;
}

/* ---------------------- Compliance item detail sheet ---------------------- */
function ComplianceSheet({ initial, workers, currentUserName, onClose, onSaved }) {
  const { isWriter } = useAuth();
  const isNew = !initial.item;
  const [worker, setWorker] = useState(initial.worker?.id || (workers[0]?.id ?? ""));
  const [form, setForm] = useState(() => ({
    id: initial.item?.id || null,
    ctype: initial.item?.ctype || "Ticket",
    name: initial.item?.name || "",
    ref_no: initial.item?.ref_no || "",
    issue_date: initial.item?.issue_date || "",
    expiry_date: initial.item?.expiry_date || "",
    doc_file_id: initial.item?.doc_file_id || null,
    doc_filename: initial.item?.doc_filename || "",
    notes: initial.item?.notes || "",
    history: initial.item?.history || [],
  }));
  const [noExpiry, setNoExpiry] = useState(!!initial.item && !initial.item.expiry_date);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const status = itemStatus({ expiry_date: noExpiry ? null : form.expiry_date });
  const ctypeLabel = (form.ctype || "Item").toUpperCase();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/compliance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("doc_file_id", data.file_id); set("doc_filename", data.filename || file.name);
    } catch (err) { setError(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const viewDoc = () => window.open(`${api.defaults.baseURL}/compliance/file/${form.doc_file_id}`, "_blank");

  const save = async () => {
    setError("");
    if (!worker) { setError("Choose a worker for this item."); return; }
    if (!form.name.trim()) { setError("Item name is required."); return; }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const docChanged = form.doc_file_id !== (initial.item?.doc_file_id || null);
      const history = [...(form.history || [])];
      if (isNew) history.push({ ts: now, text: `Item created by ${currentUserName}` });
      else history.push({ ts: now, text: `Details updated by ${currentUserName}` });
      if (docChanged && form.doc_file_id) history.push({ ts: now, text: `Document ${initial.item?.doc_file_id ? "replaced" : "uploaded"} by ${currentUserName}` });
      const item = {
        id: form.id || crypto.randomUUID(),
        ctype: form.ctype, name: form.name.trim(), ref_no: form.ref_no || "",
        issue_date: form.issue_date || null,
        expiry_date: noExpiry ? null : (form.expiry_date || null),
        doc_file_id: form.doc_file_id || null, doc_filename: form.doc_filename || "",
        notes: form.notes || "", history,
      };
      const oldWorkerId = initial.worker?.id;
      // remove from previous owner if worker changed (or on create it's simply added)
      if (!isNew && oldWorkerId && oldWorkerId !== worker) {
        const old = workers.find((w) => w.id === oldWorkerId);
        if (old) await api.put(`/operators/${old.id}`, { ...old, compliances: (old.compliances || []).filter((c) => c.id !== item.id) });
      }
      const target = workers.find((w) => w.id === worker);
      let list = target.compliances || [];
      list = (!isNew && oldWorkerId === worker)
        ? list.map((c) => (c.id === item.id ? item : c))
        : [...list, item];
      await api.put(`/operators/${target.id}`, { ...target, compliances: list });
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    if (!initial.item || !initial.worker) return;
    if (!window.confirm(`Delete "${form.name}" for ${initial.worker.name}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const w = initial.worker;
      await api.put(`/operators/${w.id}`, { ...w, compliances: (w.compliances || []).filter((c) => c.id !== form.id) });
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || "Delete failed"); }
    setSaving(false);
  };

  const lbl = "text-[13px] font-bold text-ink-primary mb-1.5 block";
  const card = "bg-surface-card border border-border rounded-[12px] p-4";
  const cardHead = "text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-muted mb-3";

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={onClose}>
      <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[560px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="compliance-sheet">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0 bg-surface-card">
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{ctypeLabel}</div>
            <h3 className="font-bold text-[22px] text-ink-primary leading-tight mt-0.5 truncate">{form.name || (isNew ? "New Item" : "Item")}</h3>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[13px] text-ink-tertiary font-mono truncate">{initial.worker?.name || (workers.find((w) => w.id === worker)?.name) || "—"}{form.ref_no ? ` · ${form.ref_no}` : ""}</span>
              <StatusPill status={status} />
            </div>
          </div>
          <button onClick={onClose} data-testid="compliance-sheet-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel shrink-0"><X size={16} weight="bold" /></button>
        </div>

        <div className="flex-1 overflow-auto bg-surface-inset p-5 space-y-4">
          {status === "expired" && (
            <div className="bg-[#FBE7E4] border border-[#F1C4BC] rounded-[12px] px-4 py-3" data-testid="compliance-expired-banner">
              <div className="text-[14px] font-bold text-[#8E1212]">Expired {fmtd(form.expiry_date)} — this item is out of date</div>
              <div className="text-[13px] text-[#9A3200] mt-1">This worker is blocked from new allocations until a replacement document is uploaded with a future expiry.</div>
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-300 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="compliance-sheet-error">{error}</div>}

          <div className={card} data-testid="compliance-item-card">
            <div className={cardHead}>Item</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Worker</label>
                <select className={inp + " w-full"} value={worker} onChange={(e) => setWorker(e.target.value)} data-testid="compliance-worker-select">
                  {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select></div>
              <div><label className={lbl}>Type</label>
                <select className={inp + " w-full"} value={form.ctype} onChange={(e) => set("ctype", e.target.value)} data-testid="compliance-type-select">
                  {CTYPES.map((t) => <option key={t}>{t}</option>)}
                </select></div>
              <div className="col-span-2"><label className={lbl}>Item name</label>
                <input className={inp + " w-full"} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. HR Licence" data-testid="compliance-name-input" /></div>
              <div><label className={lbl}>Reference #</label>
                <input className={inp + " w-full"} value={form.ref_no} onChange={(e) => set("ref_no", e.target.value)} data-testid="compliance-ref-input" /></div>
              <div><label className={lbl}>Issued</label>
                <input type="date" className={inp + " w-full"} value={form.issue_date || ""} onChange={(e) => set("issue_date", e.target.value)} data-testid="compliance-issued-input" /></div>
              <div><label className={lbl}>Expiry</label>
                <input type="date" className={inp + " w-full disabled:opacity-50"} value={noExpiry ? "" : (form.expiry_date || "")} disabled={noExpiry} onChange={(e) => set("expiry_date", e.target.value)} data-testid="compliance-expiry-input" /></div>
              <label className="flex items-center gap-2 text-[13px] text-ink-secondary cursor-pointer self-end pb-2">
                <input type="checkbox" checked={noExpiry} onChange={(e) => { setNoExpiry(e.target.checked); if (e.target.checked) set("expiry_date", ""); }} className="w-4 h-4 accent-equipr-yellow" data-testid="compliance-noexpiry-check" /> No expiry
              </label>
            </div>
          </div>

          <div className={card} data-testid="compliance-doc-card">
            <div className={cardHead}>Document</div>
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid="compliance-file-input" />
            {form.doc_file_id ? (
              <div className="flex items-center gap-3 bg-surface-inset border border-border rounded-[10px] px-3 py-2.5">
                <FileText size={22} className="text-ink-tertiary shrink-0" />
                <div className="min-w-0 flex-1">
                  <button onClick={viewDoc} data-testid="compliance-view-doc" className="text-[14px] font-semibold text-ink-primary hover:underline truncate block text-left">{form.doc_filename || "Document"}</button>
                  <div className="text-[12px] text-ink-tertiary">{(form.doc_filename?.split(".").pop() || "file").toUpperCase()} · on file</div>
                </div>
                {isWriter && <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="compliance-replace-doc" className="h-9 px-4 border border-border-control bg-white text-ink-primary text-[12px] font-bold rounded-[8px] hover:bg-surface-panel disabled:opacity-50 shrink-0">{uploading ? "…" : "Replace"}</button>}
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} disabled={!isWriter || uploading} data-testid="compliance-upload-doc"
                className="w-full border border-dashed border-border-control rounded-[10px] px-3 py-5 text-center text-ink-tertiary hover:border-accent-edge hover:text-ink-secondary disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <UploadSimple size={18} /> {uploading ? "Uploading…" : "Upload document (image or PDF, ≤10MB)"}
              </button>
            )}
          </div>

          <div className={card}>
            <div className={cardHead}>Notes</div>
            <textarea className={inp + " w-full h-40 py-2 leading-relaxed resize-y"} rows={6} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything the office needs to know about this document" data-testid="compliance-notes-input" />
          </div>

          {(form.history || []).length > 0 && (
            <div className={card} data-testid="compliance-history-card">
              <div className={cardHead}>History</div>
              <div className="space-y-2">
                {[...form.history].reverse().map((h, i) => (
                  <div key={i} className="flex gap-3 text-[13px]">
                    <span className="font-mono text-[11.5px] text-ink-tertiary shrink-0 w-[132px]">{fmtTs(h.ts)}</span>
                    <span className="text-ink-secondary">{h.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {isWriter && (
          <div className="shrink-0 border-t border-divider-inner bg-surface-card">
            <p className="px-6 pt-3 text-[12px] text-ink-tertiary">Saving updates the worker's compliance status and clears the allocation block once the expiry is in the future.</p>
            <div className="flex items-center gap-3 px-6 py-3">
              <button onClick={onClose} className="h-10 px-5 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[8px] hover:bg-surface-panel">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="compliance-save-btn" className="h-10 px-5 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[8px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
              {!isNew && <button onClick={del} disabled={saving} data-testid="compliance-delete-btn" className="ml-auto text-[13px] font-bold text-[#8E1212] hover:underline">Delete this item</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Compliance() {
  const { isWriter, user } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [ctype, setCtype] = useState("all");
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState(null); // {worker, item} | {} for new

  const load = useCallback(async () => {
    try { const { data } = await api.get("/operators"); setWorkers(data.filter((w) => w.employment_status !== "inactive")); } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = workers.flatMap((w) => (w.compliances || []).map((c) => ({ worker: w, item: c, status: itemStatus(c) })));
  const counts = rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const filtered = rows.filter((r) =>
    (status === "all" || r.status === status) &&
    (ctype === "all" || r.item.ctype === ctype) &&
    (!q || r.worker.name.toLowerCase().includes(q.toLowerCase()) || (r.item.name || "").toLowerCase().includes(q.toLowerCase()) || (r.item.ref_no || "").toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => ({ expired: 0, expiring: 1, current: 2, no_expiry: 3 }[a.status] - { expired: 0, expiring: 1, current: 2, no_expiry: 3 }[b.status]));

  const viewDoc = (e, c) => { e.stopPropagation(); window.open(`${api.defaults.baseURL}/compliance/file/${c.doc_file_id}`, "_blank"); };

  const tiles = [
    ["expired", "Expired", "text-[#B42318]"], ["expiring", "Expiring ≤30d", "text-[#7A5B00]"],
    ["current", "Current", "text-[#0E6B3F]"], ["no_expiry", "No expiry set", "text-ink-tertiary"],
  ];
  return (
    <div className="h-full flex flex-col bg-surface-page p-4">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
          <div>
            <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">Compliance Register <span className="ml-1 text-[12px] font-normal text-ink-tertiary">{rows.length} items</span></h1>
            <p className="text-[12px] text-ink-tertiary">Worker licences, tickets, inductions and medicals across the business</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
              <input className="w-64 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[9px] text-body focus:outline-none focus:ring-2 focus:ring-equipr-yellow/35" placeholder="Worker, item or ref…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="compliance-search" />
            </div>
            <select className={inp + " h-[42px]"} value={ctype} onChange={(e) => setCtype(e.target.value)} data-testid="compliance-filter-type">
              <option value="all">All types</option>{CTYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            {isWriter && (
              <button onClick={() => setSheet({})} data-testid="compliance-new-btn"
                className="h-[42px] px-4 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
                <Plus size={15} weight="bold" /> New Item
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 divide-x divide-divider-inner border-b border-divider-inner shrink-0" data-testid="compliance-filter-status">
          {tiles.map(([k, label, color]) => (
            <button key={k} onClick={() => setStatus(status === k ? "all" : k)} data-testid={`compliance-tile-${k}`}
              className={`px-5 py-3 text-left transition-colors ${status === k ? "bg-accent-tint" : "hover:bg-surface-panel"}`}>
              <div className={`text-[24px] font-extrabold leading-none ${color}`}>{counts[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-[0.1em] text-ink-muted font-bold mt-1.5">{label}</div>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-cell">
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface-sunken border-b border-border">{["Worker", "Type", "Item", "Ref #", "Issued", "Expiry", "Status", "Document"].map((h, i) => (
                <th key={h} className={`text-left px-4 py-2.5 uppercase tracking-[0.1em] text-colhead ${i === 0 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={8} />
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-ink-tertiary" data-testid="compliance-register-empty">No compliance records match{status !== "all" || ctype !== "all" || q ? " your filters" : " — add the first with New Item"}.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.item.id} onClick={() => setSheet({ worker: r.worker, item: r.item })}
                  className={`border-b border-divider-row hover:bg-accent-wash cursor-pointer ${r.status === "expired" ? "shadow-[inset_3px_0_0_#FF4F00]" : ""}`} data-testid={`compliance-register-row-${r.item.id}`}>
                  <td className="px-4 py-2.5 font-semibold text-ink-primary">{r.worker.name}</td>
                  <td className="px-4 py-2.5 text-ink-secondary">{r.item.ctype}</td>
                  <td className="px-4 py-2.5 text-ink-secondary">{r.item.name}</td>
                  <td className="px-4 py-2.5 font-mono text-mono-id text-ink-tertiary">{r.item.ref_no || "—"}</td>
                  <td className="px-4 py-2.5 text-ink-secondary">{fmtd(r.item.issue_date)}</td>
                  <td className="px-4 py-2.5 text-ink-secondary">{fmtd(r.item.expiry_date)}</td>
                  <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2.5">
                    {r.item.doc_file_id
                      ? <button onClick={(e) => viewDoc(e, r.item)} data-testid={`compliance-view-doc-${r.item.id}`} className="text-accent-ink font-bold text-xs inline-flex items-center gap-1 hover:underline"><FileText size={13} /> View</button>
                      : <span className="text-ink-disabled text-xs">No file</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
          <span data-testid="compliance-footer">{loading ? "Loading…" : `${filtered.length} item${filtered.length === 1 ? "" : "s"}${filtered.length !== rows.length ? ` · filtered from ${rows.length}` : ""}`}</span>
          <span>Expiry is checked nightly; workers with an expired item cannot be allocated.</span>
        </div>
      </div>

      {sheet && (
        <ComplianceSheet initial={sheet} workers={workers} currentUserName={user?.name || "Administrator"}
          onClose={() => setSheet(null)} onSaved={() => { setSheet(null); load(); }} />
      )}
    </div>
  );
}
